import { useMemo } from 'react';
import {
    SquareSVG,
    CircleSVG,
    TriangleSVG,
    PentagonSVG,
    StarSVG,
    CrossSVG,
    RectangleSVG,
    DiamondSVG,
    HeartSVG,
    DefaultSVG as DropoffSVG,
    Abstract1SVG,
    Abstract2SVG,
    Abstract3SVG,
    Abstract4SVG,
    Abstract5SVG,
    Abstract6SVG,
    Abstract7SVG,
    Abstract8SVG,
    Abstract9SVG,
    Abstract10SVG,
    Abstract11SVG,
    Abstract12SVG,
    Abstract13SVG,
} from '../map/Marker';
import { recommendedEquipmentLabels } from '@/constants/recommendedEquipmentLabels';
import { getBoardShippers } from '../util/shippers';

export const SHAPES = [
    'default',
    'square',
    'circle',
    'triangle',
    'pentagon',
    'star',
    'cross',
    'rectangle',
    'diamond',
    'heart',
    'abstract1',
    'abstract2',
    'abstract3',
    'abstract4',
    'abstract5',
    'abstract6',
    'abstract7',
    'abstract8',
    'abstract9',
    'abstract10',
    'abstract11',
    'abstract12',
    'abstract13',
];

export const getOrderTagShapeNames = (order, includeRecommendedEquipment = false) => {
    const routeTags = (order?.tags || []).filter(({ tag }) => tag?.route_tag).map(({ tag }) => tag.tag);

    const equipment = includeRecommendedEquipment
        ? {
              ...(order?.recommended_equipment || {}),
              ...(order?.equipment_overrides || {}),
          }
        : { ...(order?.equipment_overrides || {}) };
    const equipmentNames = Object.keys(equipment)
        .filter((key) => equipment[key])
        .map((key) => recommendedEquipmentLabels[key] || key)
        .reverse(); // overrides take priority in shape shown

    return [...new Set([...routeTags, ...equipmentNames])];
};

export const getShape = (type, options, order, includeRecommendedEquipment = false) => {
    switch (type) {
        case 'shipper':
            return options?.find((shape) => shape.name === order.shipper_id) || options?.find((shape) => !shape.name);
        case 'order_tags': {
            const tagNames = getOrderTagShapeNames(order, includeRecommendedEquipment);
            for (const name of tagNames) {
                const match = options?.find((shape) => shape.name === name);
                if (match) return match;
            }
            return undefined;
        }
        default:
            return options?.find((shape) => shape.name === order[type]);
    }
};

export const drawSVG = (shape, props) => {
    switch (shape) {
        case 'square':
            return SquareSVG(props);
        case 'circle':
            return CircleSVG(props);
        case 'triangle':
            return TriangleSVG(props);
        case 'pentagon':
            return PentagonSVG(props);
        case 'star':
            return StarSVG(props);
        case 'cross':
            return CrossSVG(props);
        case 'rectangle':
            return RectangleSVG(props);
        case 'diamond':
            return DiamondSVG(props);
        case 'heart':
            return HeartSVG(props);
        case 'abstract1':
            return Abstract1SVG(props);
        case 'abstract2':
            return Abstract2SVG(props);
        case 'abstract3':
            return Abstract3SVG(props);
        case 'abstract4':
            return Abstract4SVG(props);
        case 'abstract5':
            return Abstract5SVG(props);
        case 'abstract6':
            return Abstract6SVG(props);
        case 'abstract7':
            return Abstract7SVG(props);
        case 'abstract8':
            return Abstract8SVG(props);
        case 'abstract9':
            return Abstract9SVG(props);
        case 'abstract10':
            return Abstract10SVG(props);
        case 'abstract11':
            return Abstract11SVG(props);
        case 'abstract12':
            return Abstract12SVG(props);
        case 'abstract13':
            return Abstract13SVG(props);
        case 'default':
            return `<svg width='27' height='33' viewBox='0 0 27 33' fill='none' xmlns='http://www.w3.org/2000/svg'>
                        <mask id='path-1-inside-1_1834_43062' fill='white'><path fill-rule='evenodd' clip-rule='evenodd' d='M26.0014 0H0V18.9304H0.00385723L12.6967 32.9996L26.0013 18.9304H26.0014V18.9303L26.0033 18.9283H26.0014V0Z'/>
                        </mask>
                        <path fill-rule='evenodd' clip-rule='evenodd' d='M26.0014 0H0V18.9304H0.00385723L12.6967 32.9996L26.0013 18.9304H26.0014V18.9303L26.0033 18.9283H26.0014V0Z' fill='${props.color}'/><path d='M0 0V-2H-2V0H0ZM26.0014 0H28.0014V-2H26.0014V0ZM0 18.9304H-2V20.9304H0V18.9304ZM0.00385723 18.9304L1.48884 17.5907L0.893144 16.9304H0.00385723V18.9304ZM12.6967 32.9996L11.2117 34.3394L12.6622 35.947L14.1499 34.3738L12.6967 32.9996ZM26.0013 18.9304V16.9304H25.14L24.5481 17.5563L26.0013 18.9304ZM26.0014 18.9304V20.9304H28.0014V18.9304H26.0014ZM26.0014 18.9303L24.5481 17.5563L24.0014 18.1346V18.9303H26.0014ZM26.0033 18.9283L27.4567 20.3023L30.6464 16.9283H26.0033V18.9283ZM26.0014 18.9283H24.0014V20.9283H26.0014V18.9283ZM0 2H26.0014V-2H0V2ZM2 18.9304V0H-2V18.9304H2ZM0.00385723 16.9304H0V20.9304H0.00385723V16.9304ZM14.1817 31.6599L1.48884 17.5907L-1.48112 20.2702L11.2117 34.3394L14.1817 31.6599ZM24.5481 17.5563L11.2436 31.6255L14.1499 34.3738L27.4544 20.3046L24.5481 17.5563ZM26.0014 16.9304H26.0013V20.9304H26.0014V16.9304ZM24.0014 18.9303V18.9304H28.0014V18.9303H24.0014ZM24.55 17.5543L24.5481 17.5563L27.4548 20.3043L27.4567 20.3023L24.55 17.5543ZM26.0014 20.9283H26.0033V16.9283H26.0014V20.9283ZM24.0014 0V18.9283H28.0014V0H24.0014Z' fill='white' mask='url(#path-1-inside-1_1834_43062)'/>
                    </svg>`;
        default:
            return DropoffSVG(props);
    }
};

export const getStopShape = (stop, ordersByID, shapeFieldType, shapesOverride, includeRecommendedEquipment = false) => {
    const orderShapes = (stop.orders || []).map((id) => {
        const order = ordersByID[id];
        return order
            ? getShape(shapeFieldType, shapesOverride, order, includeRecommendedEquipment)?.shape || 'default'
            : 'default';
    });
    if (orderShapes.length === 0) return 'default';
    if (orderShapes.length === 1) return orderShapes[0];

    // If multiple non default shapes take the first
    const nonDefaults = orderShapes.filter((s) => s !== 'default');
    return nonDefaults.length === 1 ? nonDefaults[0] : orderShapes[0];
};

export const useShapeFieldTypeOptions = (orders, user_id, includeRecommendedEquipment = false) => {
    return useMemo(() => {
        const options = new Set();
        (orders || []).forEach((order) => {
            if (order.shipper_id !== user_id) {
                options.add('shipper');
            }
            if (order.template_name) {
                options.add('template_name');
            }
            if (order.manufacturer) {
                options.add('manufacturer');
            }
            if (order.item_type) {
                options.add('item_type');
            }
            if (order.custom_shape) {
                options.add('custom_shape');
            }
            if (getOrderTagShapeNames(order, includeRecommendedEquipment).length > 0) {
                options.add('order_tags');
            }
        });
        options.add('order_type');
        return [...options];
    }, [orders, user_id, includeRecommendedEquipment]);
};

export const useFieldShapesSingle = ({
    orders,
    savedShapes,
    shapeFieldType,
    showComboShapes,
    shipping_partners = [],
    user_id,
    includeRecommendedEquipment = false,
}) => {
    return useMemo(() => {
        // Two passes. The groups on the board are collected first because the shape pool cannot
        // be sized until every name is known: which saved shapes are in play decides which of
        // the 23 are still free to hand out.
        const groups = [];
        const seen = new Set();
        const add = (name, label) => {
            if (seen.has(name)) return;
            seen.add(name);
            groups.push({ name, label });
        };

        switch (shapeFieldType) {
            case 'shipper': {
                add('', 'None');

                // Building the list from partnerships alone left the modal empty on a board whose
                // shippers were all unpartnered, and listed partners with nothing on the board.
                // The client's own orders keep falling through to the None row, as they did
                // before: getBoardShippers drops them for the same reason
                // useShapeFieldTypeOptions only offers this mode when another shipper is present.
                getBoardShippers({ orders, shipping_partners, user_id }).forEach(({ shipper_id, label }) =>
                    add(shipper_id, label)
                );
                break;
            }
            case 'order_tags':
                (orders || []).forEach((order) => {
                    getOrderTagShapeNames(order, includeRecommendedEquipment).forEach((value) => {
                        if (value) add(value, value);
                    });
                });
                break;
            default:
                (orders || []).forEach((order) => {
                    const value = order[shapeFieldType];
                    if (value) add(value, undefined);
                });
                break;
        }

        // Only the saved shapes belonging to groups on this board are reserved. The saved list
        // outlives the boards it was built from -- and is merged rather than replaced on save, so
        // it never shrinks -- so reserving all of it would cost the pool a shape for every group
        // ever assigned one, until a new group could not be given a shape at all.
        const savedForField = new Map(
            (savedShapes?.[shapeFieldType] || [])
                .filter((el) => el?.name && el?.shape && seen.has(el.name))
                .map((el) => [el.name, el.shape])
        );
        const reserved = new Set(savedForField.values());
        const pool = SHAPES.filter((shape) => !reserved.has(shape));

        // Reserving up front rather than as each entry is reached keeps this independent of
        // iteration order: otherwise an entry that comes first is auto-assigned a shape a later
        // entry has saved, and two groups end up sharing a pin -- the one thing the modal exists
        // to prevent.
        return groups.map(({ name, label }) => ({
            name,
            ...(label !== undefined && { label }),
            shape: savedForField.get(name) ?? pool.shift(),
        }));
    }, [orders, savedShapes, shapeFieldType, showComboShapes, shipping_partners, user_id, includeRecommendedEquipment]);
};

export const useComboPotentialChoices = (orders, includeRecommendedEquipment = false) => {
    return useMemo(() => {
        let choices = {
            manufacturer: [],
            template_name: [],
            item_type: [],
            custom_shape: [],
            order_tags: [],
            order_type: ['delivery', 'return', 'exchange', 'service'],
        };
        Object.keys(choices).forEach((category) => {
            (orders || []).forEach((order) => {
                if (category === 'order_tags') {
                    getOrderTagShapeNames(order, includeRecommendedEquipment).forEach((name) => {
                        if (name && !choices.order_tags.includes(name)) {
                            choices.order_tags.push(name);
                        }
                    });
                    return;
                }
                if (order[category] && !choices[category].includes(order[category])) {
                    choices[category].push(order[category]);
                }
            });
        });
        return choices;
    }, [orders, includeRecommendedEquipment]);
};
